import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Copy, QrCode } from "lucide-react";
import { toast } from "sonner";
import { API, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ErrorState, Loading } from "@/components/ds";

const publicApi = axios.create({ baseURL: API });

export default function PublicPix() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const orderToken = params.get("pedido");
  const [pix, setPix] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [declared, setDeclared] = useState(false);

  const load = () => {
    setError("");
    publicApi
      .get(`/public/pix/${token}`)
      .then(({ data }) => setPix(data))
      .catch((e) => setError(apiError(e)));
  };

  useEffect(load, [token]);

  const copy = async () => {
    await navigator.clipboard.writeText(pix.brcode);
    setCopied(true);
    toast.success("Código Pix copiado.");
    setTimeout(() => setCopied(false), 2500);
  };

  const declare = async () => {
    try {
      await publicApi.post(`/public/pix/${token}/declare`);
      setDeclared(true);
      toast.success("Obrigado! Vamos conferir e confirmar o pagamento.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!pix) return <Loading label="Carregando pagamento..." />;

  const closed = pix.status !== "PENDENTE";

  return (
    <div className="min-h-screen bg-background px-5 py-10" data-testid="public-pix-page">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <img
            src="/brand/tr3nd-logo.jpg"
            alt="TR3ND"
            className="h-11 w-11 rounded-lg object-contain"
          />
          <div>
            <p className="font-display text-base font-semibold leading-none">Pagamento via Pix</p>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              {pix.origin_label} {pix.label}
            </p>
          </div>
        </div>

        <p className="font-num text-4xl text-brand" data-testid="pix-amount">
          {brl(pix.amount)}
        </p>

        {closed ? (          <p
            className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200"
            data-testid="pix-closed"
          >
            {pix.status === "CONFIRMADA"
              ? "Este pagamento já foi confirmado. Obrigado!"
              : "Esta cobrança não está mais válida porque o valor foi atualizado. Peça um novo link."}
          </p>
        ) : (
          <>
            <div className="mt-6 rounded-2xl border border-white/10 bg-card p-5">
              <img
                src={pix.qr_code}
                alt="QR Code Pix"
                className="mx-auto h-56 w-56 rounded-lg bg-white p-2"
                data-testid="pix-qr-image"
              />
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Aponte a câmera do celular para o QR Code
              </p>
            </div>

            <p className="mt-6 text-xs uppercase tracking-wide text-muted-foreground">
              Pix Copia e Cola
            </p>
            <p
              className="mt-1.5 max-h-28 overflow-y-auto break-all rounded-xl border border-white/10 bg-secondary/40 p-3 text-[11px] text-muted-foreground"
              data-testid="pix-brcode"
            >
              {pix.brcode}
            </p>
            <Button
              onClick={copy}
              data-testid="pix-copy-btn"
              className="mt-3 h-12 w-full gap-2 rounded-full font-semibold transition-transform active:scale-95"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Código copiado" : "Copiar código"}
            </Button>

            <Button
              variant="outline"
              onClick={declare}
              disabled={declared || !!pix.declared_at}
              data-testid="pix-declare-btn"
              className="mt-2 h-11 w-full gap-2 rounded-full text-xs"
            >
              <QrCode className="h-3.5 w-3.5" />
              {declared || pix.declared_at ? "Pagamento informado" : "Já paguei"}
            </Button>
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              {pix.pix_holder ? `Recebedor: ${pix.pix_holder}` : ""}
            </p>
          </>
        )}

        {orderToken && (
          <Button
            variant="ghost"
            onClick={() => window.location.assign(`/pedido/${orderToken}`)}
            data-testid="pix-back-to-order-btn"
            className="mt-6 h-11 w-full gap-2 rounded-full text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao acompanhamento do pedido
          </Button>
        )}
      </div>
    </div>
  );
}
